import { useState } from "react";
import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Users, Workflow, TrendingUp, Trophy, MessageSquareHeart, ListChecks, Target, Menu, LogOut, Clock, FileText, Wrench, KeyRound, CalendarDays, Award } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Brand } from "@/components/Brand";
import { BirthdayGreeting } from "@/components/Birthdays";
import { useStore } from "@/store/AppStore";
import { Initials } from "@/components/kit";
import { ChangePasswordDialog } from "@/components/Accounts";
import { HOME, FullLoader } from "@/pages/Login";
import { PROFILES } from "@/data/mock";

const NAV = {
  master: [
    { id: "overview", to: "/master", label: "Visão geral", icon: LayoutDashboard, end: true },
    { id: "companies", to: "/master/empresas", label: "Empresas", icon: Building2 },
    { id: "ranking", to: "/master/ranking", label: "Ranking e selo", icon: Award },
    { id: "agenda", to: "/master/agenda", label: "Agenda", icon: CalendarDays },
    { id: "report", to: "/master/relatorio", label: "Relatório mensal", icon: FileText },
  ],
  empresaria: [
    { id: "dashboard", to: "/empresaria", label: "Painel", icon: LayoutDashboard, end: true },
    { id: "team", to: "/empresaria/equipe", label: "Equipe e tarefas", icon: Users },
    { id: "attendance", to: "/empresaria/jornada", label: "Jornada e presença", icon: Clock },
    { id: "processes", to: "/empresaria/processos", label: "Processos e checklists", icon: Workflow },
    { id: "maintenance", to: "/empresaria/manutencao", label: "Manutenção", icon: Wrench },
    { id: "access", to: "/empresaria/acessos", label: "Acessos e senhas", icon: KeyRound },
    { id: "agenda", to: "/empresaria/agenda", label: "Agenda", icon: CalendarDays },
    { id: "sales", to: "/empresaria/vendas", label: "Vendas", icon: TrendingUp },
    { id: "goals", to: "/empresaria/metas", label: "Metas e resultados", icon: Target },
    { id: "ranking", to: "/empresaria/ranking", label: "Ranking mensal", icon: Trophy },
    { id: "feedback", to: "/empresaria/feedback", label: "Feedback", icon: MessageSquareHeart },
    { id: "network", to: "/empresaria/indicacoes", label: "Selo e indicações", icon: Award },
  ],
  colaboradora: [
    { id: "today", to: "/colaboradora", label: "Meu dia", icon: ListChecks, end: true },
    { id: "goals", to: "/colaboradora/metas", label: "Minhas metas", icon: Target },
    { id: "feedback", to: "/colaboradora/feedback", label: "Feedback", icon: MessageSquareHeart },
    { id: "access", to: "/colaboradora/acessos", label: "Acessos", icon: KeyRound, needsAccess: true },
  ],
};

const ROLE_LABEL = { master: "Painel Master", empresaria: "Painel da Empresária", colaboradora: "Painel da Colaboradora" };

function NavItems({ role, prefix, onNavigate }) {
  const { state } = useStore();
  const canAccess = !!state.collaborators.find((c) => c.id === PROFILES.colaboradora.collaboratorId)?.accessAllowed;
  return (
    <nav className="flex flex-col gap-1">
      {NAV[role].filter((i) => !i.needsAccess || canAccess).map((item) => (
        <NavLink
          key={item.id}
          to={item.to}
          end={item.end}
          onClick={onNavigate}
          data-testid={`${prefix}-nav-${item.id}`}
          className={({ isActive }) =>
            `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors duration-200 ${isActive ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900"}`
          }
        >
          <item.icon className="h-4 w-4 text-gold" />
          {item.label}
        </NavLink>
      ))}
    </nav>
  );
}

function SidebarBody({ role, prefix, onNavigate }) {
  const navigate = useNavigate();
  const { logout } = useStore();
  const [pw, setPw] = useState(false);
  return (
    <div className="flex h-full flex-col">
      <div className="px-2 pb-6 pt-2"><Brand /></div>
      <p className="mb-3 px-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-neutral-400">{ROLE_LABEL[role]}</p>
      <NavItems role={role} prefix={prefix} onNavigate={onNavigate} />
      <button data-testid={`${prefix}-change-password`} onClick={() => setPw(true)} className="mt-auto flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900">
        <KeyRound className="h-4 w-4" /> Alterar senha
      </button>
      <button data-testid={`${prefix}-logout`} onClick={async () => { await logout(); navigate("/"); }} className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900">
        <LogOut className="h-4 w-4" /> Sair
      </button>
      <ChangePasswordDialog open={pw} onOpenChange={setPw} />
    </div>
  );
}

export default function Shell({ role }) {
  const { user, state } = useStore();
  if (user === null) return <FullLoader />;
  if (!user) return <Navigate to="/" replace />;
  if (user.role !== role) return <Navigate to={HOME[user.role]} replace />;
  if (!state) return <FullLoader />;
  return <ShellLayout role={role} state={state} />;
}

function ShellLayout({ role, state }) {
  const [open, setOpen] = useState(false);
  const profile = PROFILES[role];
  const company = state.companies.find((c) => c.id === profile.companyId);
  const title = company ? `${profile.title} · ${company.name}` : profile.title;
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 overflow-y-auto border-r border-neutral-200 bg-white p-5 lg:block print:!hidden" data-testid="sidebar">
        <SidebarBody role={role} prefix="desktop" />
      </aside>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 bg-white p-5" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SidebarBody role={role} prefix="mobile" onNavigate={() => setOpen(false)} />
        </SheetContent>
      </Sheet>
      <div className="lg:pl-64 print:!pl-0">
        <header className="sticky top-0 z-30 border-b border-neutral-200 bg-white/80 backdrop-blur-md print:hidden">
          <div className="flex h-16 items-center justify-between gap-4 px-4 sm:px-8 lg:px-12">
            <div className="flex items-center gap-3">
              <button onClick={() => setOpen(true)} className="rounded-md p-2 hover:bg-neutral-100 lg:hidden" data-testid="mobile-menu-button" aria-label="Abrir menu">
                <Menu className="h-5 w-5" />
              </button>
              <span className="hidden text-xs font-semibold uppercase tracking-[0.2em] text-neutral-400 sm:inline">{ROLE_LABEL[role]}</span>
            </div>
            <div className="flex items-center gap-3" data-testid="header-profile">
              <div className="text-right">
                <p className="text-sm font-semibold text-neutral-900">{profile.name}</p>
                <p className="hidden text-xs text-neutral-500 sm:block">{title}</p>
              </div>
              <Initials name={profile.name || "?"} className="h-10 w-10 text-sm ring-2 ring-gold/60" />
            </div>
          </div>
        </header>
        <main className="fade-in px-4 py-10 sm:px-8 lg:px-12 lg:py-12">
          <Outlet />
        </main>
        {role !== "master" && <BirthdayGreeting role={role} companyId={profile.companyId} selfId={role === "colaboradora" ? `cb-${profile.collaboratorId}` : `ow-${profile.companyId}`} />}
      </div>
    </div>
  );
}
